// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl, FormattedMessage } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { N900 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { Card } from "@/components/wpl/card/src/";

const pageSection = css`
    margin-right: auto;
    margin-left: auto;
    min-width: 100%;
    padding: ${token("space.500")} 0 ${token("space.800")};
    clear: both;
    position: relative;
`;
const resourcesMainContainer = css`
    display: flex;
    max-width: 1200px;
    flex-direction: column;
    margin: ${token("space.0")} auto ${token("space.0")} auto;
`;

const resourcesHeader = css`
    text-align: center;
    padding-bottom: ${token("space.200")};
`;
const h2Style = css`
    font-weight: 500;
    text-align: center;
    color: ${N900};
    margin: 0;

    @media (max-width: 47.99rem) {
      font-size: 2rem;
      line-height: 2.375rem;
    }

    @media (min-width: 48rem) and (max-width: 63.99rem) {
      font-size: 2.25rem;
      line-height: 2.625rem;
    }

    @media (min-width: 64rem) {
      font-size: 2.5rem;
      line-height: 3rem;
    }
  }
`;

const cardsContainer = css`
    display: flex;
    max-width: 1200px;

    ${UNSAFE_media.below.sm} {
        flex: 12;
        flex-direction: column;
    }
`;

const resourcesCard = css`
    flex: 4;
    padding: ${token("space.200")};

    .link-wrapper span {
        font-weight: 400;
    }

    .text-section {
        margin: 0;

        h3 {
            font-size: 1.25rem;
            line-height: 1.2;
            margin-bottom: 14px;

            ${media.above.lg} {
                margin-bottom: 24px;
            }
        }

        .contentBlock-body {
            margin-bottom: 24px;
        }

        .contentBlock-linkFlex {
            margin: auto 0 0;
            a {
                margin: 0;
            }
        }
    }

    ${media.above.sm} {
        .text-section {
            height: 100%;
        }
    }
`;

const ResourcesSection = () => {
    const intl = useIntl();

    const cardData = [
        {
            imageLink:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_BlueDemoVideo.webp",
            imageAltText: intl.formatMessage({
                id: "confluence.section.resources.card0.image.alt",
                defaultMessage: "Demos"
            }),
            bodyText: intl.formatMessage({
                id: "confluence.section.resources.get-started.card0.body",
                defaultMessage: "See Confluence in action and learn the basics."
            }),
            headerText: intl.formatMessage({
                id: "confluence.section.resources.card0.header",
                defaultMessage: "Demos"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "confluence.section.resources.get-started.card0.primary.link",
                    defaultMessage: "Explore demos"
                }),
                dataLabelEnglish: "Explore demos",
                link: useLocalizedHref("/software/confluence/demo")
            }
        },
        {
            imageLink:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_BlueUseCases.webp",
            imageAltText: intl.formatMessage({
                id: "confluence.section.resources.card1.image.alt",
                defaultMessage: "Use cases"
            }),
            bodyText: intl.formatMessage({
                id: "confluence.section.resources.get-started.card1.body",
                defaultMessage:
                    "Discover how Confluence can be shaped to meet your team’s needs."
            }),
            headerText: intl.formatMessage({
                id: "confluence.section.resources.card1.header",
                defaultMessage: "Use cases"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "confluence.section.resources.get-started.card1.primary.link",
                    defaultMessage: "Learn about use cases"
                }),
                dataLabelEnglish: "Learn about use cases",
                link: useLocalizedHref("/software/confluence/use-cases")
            }
        },
        {
            imageLink:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_BlueBestPractices.webp",
            imageAltText: intl.formatMessage({
                id: "confluence.section.resources.card2.image.alt",
                defaultMessage: "Best practices"
            }),
            bodyText: intl.formatMessage({
                id: "confluence.section.resources.get-started.card2.body",
                defaultMessage: "Learn how to get the most out of Confluence."
            }),
            headerText: intl.formatMessage({
                id: "confluence.section.resources.card2.header",
                defaultMessage: "Best practices"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "confluence.section.resources.get-started.card2.primary.link",
                    defaultMessage: "Read product guides"
                }),
                dataLabelEnglish: "Read product guides",
                link: useLocalizedHref("/software/confluence/resources/guides")
            }
        }
    ];
    return (
        <section>
            <div css={pageSection}>
                <div css={resourcesMainContainer}>
                    <div css={resourcesHeader}>
                        <h2 css={h2Style}>
                            <FormattedMessage
                                id="confluence.section.resources.get-started.header"
                                defaultMessage="Resources to help you get started"
                            />
                        </h2>
                    </div>

                    <div css={cardsContainer}>
                        {cardData.map((card, index) => (
                            <div css={resourcesCard} key={index}>
                                <Card
                                    key={index}
                                    imageLink={card.imageLink}
                                    imageAltText={card.imageAltText}
                                    bodyText={card.bodyText}
                                    headerText={card.headerText}
                                    aspectRatio="16/9"
                                    ctaProps={{
                                        primaryLinkProps: {
                                            type: "basic-underline",
                                            doesArrowAnimate: true,
                                            customLinkColor: "b400",
                                            text: card.primaryLink.text,
                                            href: card.primaryLink.link,
                                            dataEventProps: {
                                                "data-product-key":
                                                    "confluence",
                                                "data-event-component":
                                                    "linkButton",
                                                "data-event-container":
                                                    "resourcesSection",
                                                "data-event": "clicked",
                                                "data-label":
                                                    card.primaryLink.text,
                                                "data-label-english":
                                                    card.primaryLink
                                                        .dataLabelEnglish
                                            }
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ResourcesSection;
